import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { Link } from "react-router";
import type { AllergenDeadlineDto } from "../../../shared/nutrition-addon";
import { api } from "../../api/client";
import { ICON } from "../../components/ui";

// Allergens of public products the restaurant has not confirmed (owner 2026-11-03: no deadline any more; guests see
// Meniova's estimates marked "neconfirmați"). A band on every page keeps reminding until they are confirmed.

export function useAllergenDeadlines(rid: string) {
  return useQuery({
    queryKey: ["nutrition", rid, "allergens"],
    queryFn: () =>
      api<{ deadlines: AllergenDeadlineDto[] }>("GET", `/restaurants/${rid}/nutrition/allergens`).then(
        (r) => r.deadlines,
      ),
    staleTime: 60_000,
    refetchInterval: 10 * 60_000,
  });
}

const prod = (k: number) => (k === 1 ? "1 produs" : k < 20 ? `${k} produse` : `${k} de produse`);

function text(list: AllergenDeadlineDto[]): { title: string; body: string } | null {
  if (!list.length) return null;
  const n = list.length;
  return {
    title: `${prod(n)} ${n === 1 ? "are" : "au"} alergenii neconfirmați`,
    body: `Clienții văd alergenii estimați de Meniova, marcați „neconfirmați de restaurant”. Verifică-i și confirmă-i: răspunderea pentru informațiile despre alergeni este a restaurantului.`,
  };
}

/**
 * Compact band on every page while products of the public menu have unconfirmed allergens (admin review
 * 2026-11-02): how many, "Verifică și confirmă"; why, in a closed details.
 */
export function AllergenBanner({ rid, hidden }: { rid: string; hidden: boolean }) {
  const q = useAllergenDeadlines(rid);
  const list = q.data ?? [];
  if (!list.length || hidden) return null;
  const n = list.length;
  return (
    <section className="band band-warning" aria-label="Alergeni de confirmat">
      <TriangleAlert {...ICON} aria-hidden="true" color="var(--color-warning)" />
      <span className="band-t">{prod(n)}: alergeni neconfirmați · clienții văd estimările Meniova</span>
      <Link to="/app/nutrition/alergeni" className="btn btn-primary btn-sm">
        Verifică și confirmă
      </Link>
      <details>
        <summary>De ce</summary>
        <p style={{ margin: "4px 0 0" }}>
          Pentru fiecare preparat, Meniova estimează alergenii din rețeta tipică și îi arată clienților cu
          mențiunea „estimați de Meniova, neconfirmați de restaurant”. Rețeta ta poate fi diferită: verifică
          fiecare produs și confirmă alergenii. Abia atunci apar ca „declarați de restaurant”.
        </p>
      </details>
    </section>
  );
}

/** The same state inside "Valori nutriționale", with the way to the wizard when nothing is pending. */
export function AllergenSummary({ rid }: { rid: string }) {
  const q = useAllergenDeadlines(rid);
  const t = text(q.data ?? []);
  return (
    <section className="card" aria-label="Alergeni" style={{ padding: 16 }}>
      <div className="row" style={{ gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>
            {t ? t.title : "Alergenii de pe meniul public sunt confirmați"}
          </p>
          <p className="caption muted" style={{ margin: "4px 0 0" }}>
            {t
              ? t.body
              : "Când un produs nu are alergenii confirmați, clienții văd estimarea Meniova, marcată „neconfirmată de restaurant”."}
          </p>
        </div>
        <Link to="/app/nutrition/alergeni" className={`btn ${t ? "btn-primary" : "btn-secondary"} btn-sm`}>
          Confirmă sau modifică alergenii
        </Link>
      </div>
    </section>
  );
}
